/**
 * What every analytics report on a screen shares: the brand's client and words, the range and its
 * previous period, and how to name and link an item. Built once per screen from the studio and the
 * range in the URL; reports read it, never the URL or the studio themselves.
 */

import { useMemo } from "react";
import type { AdminClient } from "@/api/client";
import { useStudio } from "@/shell/studio";
import type { Pack } from "@/vocabulary/vocabulary";
import { useItemNames, type ItemRef } from "./item-names";
import { previousRange, rangeFor, type DateRange, type RangeDays } from "./range";

export interface ReportScope {
  client: AdminClient;
  brand: string;
  pack: Pack;
  days: RangeDays;
  range: DateRange;
  previous: DateRange;
  nameOf: (itemId: string) => ItemRef;
}

export function useReportScope(days: RangeDays): ReportScope {
  const { client, pack, items } = useStudio();
  const brand = client.brand.id;
  const nameOf = useItemNames(brand, items, days);
  // The range is worked out once per day choice, so a re-render never asks for a new range.
  const range = useMemo(() => rangeFor(days), [days]);
  const previous = useMemo(() => previousRange(range), [range]);
  return useMemo(() => ({ client, brand, pack, days, range, previous, nameOf }), [client, brand, pack, days, range, previous, nameOf]);
}
